import React, { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, Button, Stack } from '@mui/material';

import { useSelector } from '../store/utils';

import { ControlBox } from '../atoms/control-box';
import { WallboxWizard } from './wallbox-wizard/wallbox-wizard';
import { Wallbox } from '../../../core/src/shared/enums';

export const WallboxTypeSelect = () => {
  const [wizardOpen, setWizardOpen] = useState(false);

  const wallboxType = useSelector((s) => s.variables.WB_TYPE);

  const wizardWallboxInfo = useWizardWallboxInfo();

  const wizardBox = (
    <Stack gap={0.75}>
      {wallboxType !== Wallbox.None && wizardWallboxInfo}
      <Button
        variant={wallboxType === Wallbox.None ? 'contained' : 'outlined'}
        size="small"
        onClick={() => setWizardOpen(true)}
      >
        <FormattedMessage
          id="wallbox_wizard.open"
          defaultMessage="Nastavit wallbox"
        />
      </Button>
    </Stack>
  );

  return (
    <>
      <ControlBox
        control={wizardBox}
        title={
          <FormattedMessage
            id="settings.service.wallbox_type"
            defaultMessage="Typ wallbox"
          />
        }
        visibility={{ variableId: 'WB_INSTALL' }}
        fullWidth
      />

      <WallboxWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </>
  );
};

const useWizardWallboxInfo = () => {
  const wallboxType = useSelector((s) => s.variables.WB_TYPE);
  const wallboxConfig = useSelector((s) => s.variables.WALLBOX_CONFIG || {});

  // If Next type and wallboxes in config, show all names separated by comma
  // Check both enum value and string value for compatibility
  if (wallboxType === Wallbox.Next && Object.keys(wallboxConfig).length > 0) {
    return (
      <Box sx={{ '& span:not(:last-child):after': { content: '", "' } }}>
        {Object.values(wallboxConfig).map((config, index) => {
          const wallboxTypeValue = config.type as Wallbox;
          const mapped = wallboxMapping[wallboxTypeValue];
          return mapped ? <span key={index}>{mapped}</span> : null;
        })}
      </Box>
    );
  }

  // Fallback to regular mapping
  const mapped = wallboxMapping[wallboxType as Wallbox];
  return <Box>{mapped || null}</Box>;
};

const wallboxMapping: Record<Wallbox, ReactNode> = {
  [Wallbox.None]: <FormattedMessage id="wallbox.none" defaultMessage="Žádný" />,
  [Wallbox.Solax]: (
    <FormattedMessage id="wallbox.solax" defaultMessage="Solax" />
  ),
  [Wallbox.SolaxEvCharger]: (
    <FormattedMessage
      id="wallbox.solax-ev-charger"
      defaultMessage="Solax - komunikace přes Infigy"
    />
  ),
  [Wallbox.GoodweEvCharger]: (
    <FormattedMessage id="wallbox.goodwe-ev-charger" defaultMessage="Goodwe" />
  ),
  [Wallbox.EvMate]: (
    <FormattedMessage id="wallbox.evmate" defaultMessage="EVmate" />
  ),
  [Wallbox.VictronEvCharger]: (
    <FormattedMessage
      id="wallbox.victron-ev-charger"
      defaultMessage="Victron EV Charger"
    />
  ),
  [Wallbox.Alfen]: (
    <FormattedMessage id="wallbox.alfen" defaultMessage="Alfen" />
  ),
  [Wallbox.MyBox]: (
    <FormattedMessage id="wallbox.mybox" defaultMessage="MyBox" />
  ),
  [Wallbox.Olife]: (
    <FormattedMessage id="wallbox.olife" defaultMessage="Olife" />
  ),
  [Wallbox.Ecovolter]: (
    <FormattedMessage id="wallbox.ecovolter" defaultMessage="EcoVolter" />
  ),
  [Wallbox.Next]: null, // Not used directly, types come from WALLBOX_CONFIG
  [Wallbox.AzRouter]: (
    <FormattedMessage id="wallbox.az_router" defaultMessage="AZ Router" />
  ),
};
